import { StatusColor } from '@interacta-shared/util';
import { i18nStringV2 } from '@interacta-shared/util-common';

export interface IBaseExternalLink {
    id: number;
    label: string;
    url: string;
    coloredIconSrc?: string;
}

export interface ILink<T = string> {
    id?: number;
    name: i18nStringV2;
    url?: T;
    communityId?: number;
    /**
     * `publishedHomeId`
     */
    homeId?: number;
    temporaryContentPreviewImageLink?: string;
    temporaryContentViewLink?: string;
    colorCode?: string | null;
}

export enum LinkOpenMode {
    EMBEDDED = 1,
    SYSTEM = 2,
}

export enum IExternalLinksCategoryType {
    'GUIDE_CENTER' = 'GUIDE_CENTER',
    'XLEARNING' = 'X-LEARNING',
    'CHAT' = 'CHAT',
    'ADMINISTRATION' = 'ADMINISTRATION',
    'SINGLE_LINK' = 'SINGLE_LINK',
    'MULTIPLE_LINK' = 'MULTIPLE_LINK',
}

type SuiteType =
    | IExternalLinksCategoryType.GUIDE_CENTER
    | IExternalLinksCategoryType.XLEARNING
    | IExternalLinksCategoryType.ADMINISTRATION
    | IExternalLinksCategoryType.CHAT;

export const isSuiteType = (
    type: IExternalLinksCategoryType,
): type is SuiteType => {
    return [
        IExternalLinksCategoryType.GUIDE_CENTER,
        IExternalLinksCategoryType.XLEARNING,
        IExternalLinksCategoryType.ADMINISTRATION,
        IExternalLinksCategoryType.CHAT,
    ].includes(type);
};

const SUITE_IMAGE_MAP: Record<SuiteType, string> = {
    [IExternalLinksCategoryType.CHAT]:
        './assets/images-2.0/suite-links/chat-link.jpg',
    [IExternalLinksCategoryType.XLEARNING]:
        './assets/images-2.0/suite-links/xlearning-link.jpg',
    [IExternalLinksCategoryType.GUIDE_CENTER]:
        './assets/images-2.0/suite-links/guidecenter-link.jpg',
    [IExternalLinksCategoryType.ADMINISTRATION]:
        './assets/images-2.0/suite-links/adminconsole-link.jpg',
};

export const getSuiteImageUrl = (
    type: IExternalLinksCategoryType,
): string | undefined => {
    return isSuiteType(type) ? SUITE_IMAGE_MAP[type] : undefined;
};

export const adminSuiteImageUrl = getSuiteImageUrl(
    IExternalLinksCategoryType.ADMINISTRATION,
);

export interface IExternalLinksCategory {
    id: number;
    label: string;
    type: IExternalLinksCategoryType;
    links: IExternalLink[];
}

export interface IExternalLink extends IBaseExternalLink {
    openMode: LinkOpenMode;
    authRequired: boolean;
    pinned: boolean;
    imageUrl?: string;
    colorCode?: string;
}

export const COLOR_CODE_MAP: Map<StatusColor, string> = new Map([
    ['A', 'var(--in-theme-status-A)'],
    ['B', 'var(--in-theme-status-B)'],
    ['C', 'var(--in-theme-status-C)'],
    ['D', 'var(--in-theme-status-D)'],
    ['E', 'var(--in-theme-status-E)'],
    ['F', 'var(--in-theme-status-F)'],
    ['G', 'var(--in-theme-status-G)'],
    ['H', 'var(--in-theme-status-H)'],
    ['I', 'var(--in-theme-status-I)'],
    ['J', 'var(--in-theme-status-J)'],
    ['K', 'var(--in-theme-status-K)'],
    ['L', 'var(--in-theme-status-L)'],
    ['M', 'var(--in-theme-status-M)'],
    ['N', 'var(--in-theme-status-N)'],
    ['O', 'var(--in-theme-status-O)'],
]);

export interface NormalizedLink {
    /**
     * Number for external-link id, string for useful-link uuid
     */
    id: number | string;
    label: string;
    linkType: 'external' | 'useful';
    url?: string;
    communityId?: number;
    imageUrl?: string;
    colorCode?: string;
    pinned?: boolean;
    authRequired?: boolean;
    /**
     * For grouped useful links: array of sub-links.
     * When present, this link represents a group and should be rendered differently.
     */
    groupedLinks?: NormalizedLink[];
}

/** Narrowed view for external links — id is always numeric, pin/auth fields always present. */
export type ExternalNormalizedLink = NormalizedLink & {
    linkType: 'external';
    id: number;
    url: string;
    pinned: boolean;
    authRequired: boolean;
};

/** Narrowed view for useful links — id is always a string uuid. */
export type UsefulNormalizedLink = NormalizedLink & {
    linkType: 'useful';
    id: string;
    communityId: number;
};

export interface NormalizedLinkGroup {
    id: string;
    links: NormalizedLink[];
    label?: string;
}

export interface NormalizedLinkSection {
    id: string;
    label: string;
    groups: NormalizedLinkGroup[];
}
